<template>
  <div class="app" :style="breadcrumbBorderStyle">
    <div
      class="app-snackbar position-fixed fixed-bottom m-3 m-md-5 py-5 d-flex justify-content-center w-100"
    >
      <app-snackbar />
    </div>
    <vue-progress-bar />
    <div class="site-wrapper">
      <deu-header
        project="hub"
        active-menu-item="data"
        :path="headerPathForDeuHeader"
        :enable-authentication="isAuthEnabled"
        :authenticated="isAuthenticated"
        :dashboard-base-url="dashboardBaseUrl"
        :get-dashboard-token="getDashboardToken"
        use-breadcrumbs
        use-breadcrumbs-route-meta
        @login="doLogin"
        @logout="doLogout"
        :showSparql="showSparql"
        :preferred_username="getUserData.preferred_username"
        :email="getUserData.email"
      />
      <router-view class="content" :key="$route?.fullPath" />
      <deu-footer
        :enable-authentication="isAuthEnabled"
        :authenticated="isAuthenticated"
        :use-login="$env.authentication.login.useLogin"
        :login="$env.authentication.login.loginURL"
        :login-title="$env.authentication.login.loginTitle"
        :logout="$env.authentication.login.logoutURL"
        :logout-title="$env.authentication.login.logoutTitle"
        @click-follow-link="handleFollowClick"
      />
    </div>
    <dpiMenu v-if="isAuthenticated">
      <template #right="{ getUserData }">
        <small class="text-white"
          >{{ $t("message.dataupload.menu.loggedInAs") }}
          {{ getUserData.userName }}</small><br/>
        <button type="button" class="btn btn-outline-light" @click="doLogout">
          {{ $t("message.dataupload.menu.logout") }}
        </button>
      </template>
    </dpiMenu>
    <cookie-consent :piwik-instance="$piwik" />
    <LoginRedirect />
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { defineComponent, computed, getCurrentInstance, watch, ref } from "vue";
import {
  usePvAuth,
  LoginRedirect,
  useRptQuery,
} from "@deu/piveau-hub-ui-plugin-auth";
import CookieConsent from "./cookie-consent/CookieConsent";
import { DpiMenu, useRuntimeEnv } from "@piveau/piveau-hub-ui-modules";
import { useStore } from "vuex";
import {
  patchAxiosDatasetSearchForHvdTreeSelect,
  patchAxiosDatasetSearchWithOpenDataPresenceFacet,
} from "./utils/utils";
import axios from "axios";

export default defineComponent({
  name: "app",
  components: {
    CookieConsent,
    DpiMenu,
    LoginRedirect,
  },
  metaInfo() {
    return {
      titleTemplate(chunk) {
        return chunk
          ? `${chunk} - ${this.$env.metadata.title}`
          : this.$env.metadata.title;
      },
      meta: [
        {
          name: "description",
          vmid: "description",
          content: this.$env.metadata.description,
        },
        {
          name: "keywords",
          vmid: "keywords",
          content: this.$env.metadata.keywords,
        },
        {
          name: "robots",
          vmid: "robots",
          content: this.robotsContent,
        },
      ],
      htmlAttrs: {
        lang: this.$route.query.locale,
      },
    };
  },
  data() {
    return {
      tracker: null,
      matomoURL: this.$env.tracker.trackerUrl,
      piwikId: this.$env.tracker.siteId,
      lastRoute: null,
      keycloak: this.$keycloak,
      showSparql: this.$env.routing.navigation.showSparql,
    };
  },
  watch: {
    $route(to) {
      if (to.path.endsWith("/embed-snippet") || to.path.endsWith("/embed")) {
        document.body.classList.add("embed-view");
      } else {
        document.body.classList.remove("embed-view");
      }
    },
  },
  computed: {
    headerPathForDeuHeader() {
      const routePath = this.$route?.path || "";
      if (/^\/datasets\/advanced(?:\/|$)/.test(routePath)) return routePath;
      const isDetailsPage = /^\/(datasets|catalogues|datasetseries|dataservices)\/.+/.test(routePath);
      if (!isDetailsPage) return routePath;
      return routePath.startsWith("/data/") ? routePath : `/data${routePath}`;
    },
    robotsContent() {
      const route = this.$route;
      const normalizedPath = ((route && route.path) || "").toLowerCase();
      const matchedRoutes = (route && route.matched) || [];
      const routeSpecificRobots = [...matchedRoutes]
        .reverse()
        .find((r) => r?.meta?.robots)?.meta?.robots;

      const isRssPage = normalizedPath.includes("/feeds/") || normalizedPath.endsWith(".rss");
      const isMetadataPage = normalizedPath.includes("/metadata/") || normalizedPath.endsWith("/metadata");

      if (routeSpecificRobots) return routeSpecificRobots;
      if (isRssPage || isMetadataPage) return "noindex";
      return "index, follow";
    },
    authEnabled() {
      return (
        this.$env?.authentication?.useService &&
        this.$env.authentication?.login?.useLogin
      );
    },
    breadcrumbBorderStyle() {
      const isNotFoundPage = this.$route.path.match(/^\/(data\/)?404$/);
      const isDetailPage = this.$route.path.match(
        /^\/(data\/)?(datasets|catalogues)\/[a-fA-F0-9-]{36}/ // UUID Pattern
      ) || this.$route.path.match(
        /^\/(data\/)?(datasets|catalogues)\/(?!advanced)[a-zA-Z0-9-]+/ // Nicht "advanced"
      );
      return {
        '--breadcrumb-border': isDetailPage || isNotFoundPage ? '0px' : '1px'
      };
    }
  },
  methods: {
    euLogin() {
      window.location.href = "https://data.europa.eu/euodp/data/user/login";
    },
    euLogout() {},
    handleFollowClick(url) {
      this.$piwik.trackOutlink(url);
    },
  },
  setup() {
    const pvAuth = usePvAuth();
    const vm = getCurrentInstance();
    const env = useRuntimeEnv();
    const DASHBOARD_IDP = "eulogin";
    const DASHBOARD_AUDIENCE =
      env?.authentication?.login?.dashboardAudience || "";

    const url = window.location.href;
    const parsedUrl = new URL(url);
    const pathParts = parsedUrl.pathname.split("/");
    const catalogId = pathParts.filter((part) => part !== "").pop();

    //temporarily using window.location.href
    // const route = useRoute();
    // const catalogId = computed(() => {
    //   return route.params.ctlg_id; // Access the ctlg_id from route params
    // });

    if (!pvAuth) return;
    if (!vm) return;

    const store = useStore();
    let dashboardTokenPromise = null;
    let lastDashboardToken = "";

    const normalizeBaseUrl = (url) => (url ? url.replace(/\/+$/, "") : "");
    const dashboardBaseUrl = computed(() =>
      normalizeBaseUrl(env?.dashboard?.baseUrl)
    );

    const buildAuthMiddlewareHeaders = () => {
      if (typeof window === "undefined") return {};
      const csrf =
        window.localStorage.getItem("piveau-hub-ui-plugin-auth--csrf") || "";
      return csrf ? { "X-PIVEAU-CSRF-PROTECTION": csrf } : {};
    };

    const ensureDashboardAuthReady = async () => {
      if (!pvAuth?.authData?.value?.isAuthenticated) return false;

      const hasReadyAuthState = () => {
        const authState = pvAuth?.authData?.value;
        const hasBootstrapData =
          Boolean(authState?.idTokenParsed) ||
          (typeof authState?.username === "string" &&
            authState.username.trim().length > 0);
        const hasRpt =
          typeof authState?.rpt === "string" && authState.rpt.trim().length > 0;
        return hasBootstrapData && hasRpt;
      };

      if (hasReadyAuthState()) return true;
      if (!pvAuth?.refreshAuthData) return false;

      try {
        await pvAuth.refreshAuthData();
      } catch (error) {
        console.warn("Could not refresh auth state before dashboard token:", error);
      }

      return hasReadyAuthState();
    };

    const getDashboardToken = async (options = {}) => {
      const forceRefresh = Boolean(options?.forceRefresh);
      if (!pvAuth?.apiClient?.token) return "";

      const authReady = await ensureDashboardAuthReady();
      if (!authReady) return "";

      if (forceRefresh) {
        dashboardTokenPromise = null;
      }

      if (!dashboardTokenPromise) {
        dashboardTokenPromise = pvAuth.apiClient
          .token(
            {
              audience: DASHBOARD_AUDIENCE,
              idp: DASHBOARD_IDP,
            },
            {
              headers: buildAuthMiddlewareHeaders(),
              withCredentials: true,
            }
          )
          .then((response) => (response?.access_token || "").trim())
          .catch((error) => {
            console.warn(
              "Could not fetch dashboard token for notifications:",
              {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.message,
              }
            );
            // If a force refresh fails due session mismatch, keep using the last known
            // token so header-side request retry can still reach cookieless fallback.
            if (forceRefresh && lastDashboardToken) {
              return lastDashboardToken;
            }
            return "";
          })
          .then((token) => {
            if (token) {
              lastDashboardToken = token;
            }
            return token;
          })
          .finally(() => {
            dashboardTokenPromise = null;
          });
      }

      return dashboardTokenPromise;
    };

    function updateUserData(payload) {
      store.dispatch("auth/updateUserData", payload);
    }

    const { data: rpt, isSuccess: isRptSuccess } = useRptQuery();

    const successOnce = ref(false);
    watch(
      isRptSuccess,
      (success) => {
        if (!success) return;
        if (successOnce.value) return;
        successOnce.value = true;
        store.dispatch("snackbar/showSnackbar", {
          message: "Successfully logged in",
          variant: "success",
        });

        if (!rpt.value?.access_token) {
          store.dispatch("snackbar/showSnackbar", {
            message:
              "You do not have permissions to access any catalogue, please contact the administrators.",
            variant: "error",
            timeout: 12000,
          });
        }
      },
      { immediate: true }
    );

    watch(rpt, (newRpt) => {
      if (newRpt.access_token) {
        updateUserData({
          authToken: "__authtoken__",
          rtpToken: newRpt.access_token,
          hubUrl: env.api.hubUrl,
        });
      }
    });

    async function doLogin() {
      vm.appContext.config.globalProperties.$Progress.start();

      try {
        await pvAuth.login();
      } catch (error) {
        store.dispatch("snackbar/showSnackbar", {
          message: `An error occurred while attempting to move to the login page: ${error.message}`,
          variant: "error",
        });
        console.error("Error while logging in", error);
      } finally {
        vm.appContext.config.globalProperties.$Progress.finish();
      }
    }

    function doLogout() {
      pvAuth.logout();
    }

    const isAuthEnabled = computed(() => pvAuth.options.enabled);
    const isAuthenticated = computed(
      () => pvAuth.authData.value.isAuthenticated
    );

    patchAxiosDatasetSearchWithOpenDataPresenceFacet({
      axiosInstance: axios,
      openDataPresence: `${catalogId}`,
      hubSearchApiBaseUrl: env.api.baseUrl,
    });

    patchAxiosDatasetSearchForHvdTreeSelect({
      axiosInstance: axios,
      hubSearchApiBaseUrl: env.api.baseUrl,
    });

    return {
      doLogin,
      doLogout,
      isAuthenticated,
      isAuthEnabled,
      dashboardBaseUrl,
      getDashboardToken,
      isRptSuccess,
      pvAuth,
      getUserData: computed(() => {
        const authData = pvAuth.authData.value || {};
        const rawUserData = authData.userData;
        const tokenData = authData.idTokenParsed || {};

        const userData =
          rawUserData && typeof rawUserData === "object"
            ? { ...rawUserData }
            : tokenData && typeof tokenData === "object"
              ? { ...tokenData }
              : {};

        const username =
          userData.preferred_username ||
          userData.username ||
          authData.username ||
          tokenData.preferred_username ||
          null;

        const email =
          userData.email ||
          userData.mail ||
          authData.email ||
          authData.mail ||
          tokenData.email ||
          null;

        if (username) {
          userData.preferred_username = username;
          userData.userName = userData.userName || username;
        }

        if (email) {
          userData.email = email;
        }

        return userData;
      }),
    };
  },
});
</script>

<style lang="scss">
// Normalizes default css rules. See: https://github.com/necolas/normalize.css
@import "./styles/utils/normalize.css";

@font-face {
  font-family: "Ubuntu";
  src: local("Ubuntu"),
    url(../static/fonts/Ubuntu/Ubuntu-Regular.ttf) format("truetype");
}

.breadcrumbs__horizontal-line {
  border-top: var(--breadcrumb-border) solid #d1d1d1 !important;
  margin: 0 8px;
  flex-grow: 1;
}

// Embed view customization
.embed-view .deu-header-footer,
.embed-view .deu-header {
  display: none !important;
}

* {
  box-sizing: border-box;
}

.site-wrapper header {
  display: initial;
}

.container-fluid {
  max-width: 1340px !important;
}

.sfo-container {
  margin-left: 2px !important;
  margin-top: -10px !important;
  margin-bottom: 40px;
}

.ecl-select__icon.ec-ds-scope-select {
  background-color: #0e47cb !important;
}

.ecl-select__icon:hover {
  background-color: #0e47cb !important;
}

.value-display:hover,
#select-default:hover,
.ecl-select__container:hover {
  .ecl-select__icon {
    background-color: #3e6cd5 !important;
  }
}

.value-display:focus,
#select-default:focus,
.ecl-select__container:focus {
  border: 1px solid #0e47cb !important;

  .ecl-select__icon {
    background-color: #0e47cb !important;
  }
}
.router-link-inactive {
  color: #7f7f7f !important;
}

</style>

<style lang="scss" scoped>
.app {
  background-color: #fff;
}

.site-wrapper {
  border: 0;
  max-width: none;
  box-shadow: none;
  display: initial;

  .content {
    padding: 30px 30px 0 30px;
    margin-top: 15px;
    margin-bottom: 15px;
    background-color: white;
  }
}

.app-snackbar {
  z-index: 9999;
  pointer-events: none;
}

// .navbar, .header, .main-header {
//   box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
// }

</style>
